// ─────────────────────────────────────────────────────────
// components/Toast.tsx  —  Stacked toast notifications
//
// UX fix: up to 3 toasts stack simultaneously — new ones
// animate in from bottom, old ones stay visible until
// their own timer expires. Each has a dismiss button.
// Matches monolith colour semantics exactly.
// ─────────────────────────────────────────────────────────
import { useUIStore, type ToastType } from '@/store/uiStore'

interface ToastConfig {
  bg:     string
  border: string
  icon:   string
}

const CONFIG: Record<ToastType, ToastConfig> = {
  success: { bg: '#0D3B47', border: '#16A34A', icon: '✓' },
  error:   { bg: '#1a0a0a', border: '#DC2626', icon: '✕' },
  warning: { bg: '#1a1000', border: '#D97706', icon: '⚠' },
  info:    { bg: '#0D3B47', border: '#0898A8', icon: 'i' },
}

export default function Toast() {
  const { toasts, removeToast } = useUIStore()

  // Show at most 3 — oldest ones hidden gracefully
  const visible = toasts.slice(-3)

  return (
    <>
      <style>{`
        @keyframes toast-in {
          from { transform: translateY(12px) scale(.97); opacity: 0; }
          to   { transform: translateY(0)    scale(1);   opacity: 1; }
        }
        @keyframes toast-out {
          to { transform: translateY(8px) scale(.96); opacity: 0; }
        }
        .toast-item {
          animation: toast-in .2s cubic-bezier(.34,1.3,.64,1);
        }
      `}</style>

      <div style={{
        position:      'fixed',
        bottom:        20,
        right:         20,
        zIndex:        9999,
        display:       'flex',
        flexDirection: 'column',
        gap:           7,
        pointerEvents: 'none',
        maxWidth:      340,
        width:         'calc(100vw - 40px)',
      }}>
        {visible.map((t) => {
          const cfg = CONFIG[t.type]
          return (
            <div
              key={t.id}
              className="toast-item"
              style={{
                background:    cfg.bg,
                color:         '#fff',
                padding:       '9px 12px',
                borderRadius:  5,
                fontSize:      12.5,
                fontWeight:    500,
                boxShadow:     '0 4px 20px rgba(0,0,0,.3)',
                fontFamily:    'var(--font-body)',
                borderLeft:    `3px solid ${cfg.border}`,
                display:       'flex',
                alignItems:    'center',
                gap:           9,
                pointerEvents: 'all',
              }}
            >
              {/* Icon badge */}
              <div style={{
                width:         18,
                height:        18,
                borderRadius:  '50%',
                background:    cfg.border,
                display:       'flex',
                alignItems:    'center',
                justifyContent:'center',
                fontSize:      9,
                fontWeight:    800,
                flexShrink:    0,
                color:         '#fff',
              }}>{cfg.icon}</div>

              {/* Message */}
              <span style={{ flex: 1, lineHeight: 1.4 }}>{t.message}</span>

              {/* Dismiss */}
              <button
                onClick={() => removeToast(t.id)}
                aria-label="Dismiss"
                style={{
                  background:  'none',
                  border:      'none',
                  color:       'rgba(255,255,255,.45)',
                  cursor:      'pointer',
                  fontSize:    14,
                  padding:     '0 2px',
                  lineHeight:  1,
                  flexShrink:  0,
                  transition:  'color .1s',
                }}
                onMouseEnter={e => (e.currentTarget.style.color = '#fff')}
                onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,.45)')}
              >×</button>
            </div>
          )
        })}
      </div>
    </>
  )
}
